import {
  DOSE_UNITS,
  FREQUENCY_PERIOD_UNITS,
  FREQUENCY_PRESETS,
  PERIOD_UNIT_NOUNS,
  TIMING_OPTIONS,
  formatUnitLabel,
  getFrequencyPreset,
} from '@constants/medication-dosage';

import type {
  Dosage,
  DosageInput,
  DoseAndRateInput,
  RatioInput,
  Repeat,
  RepeatInput,
  TimingInput,
} from 'generated/types';

const UCUM_SYSTEM = 'http://unitsofmeasure.org';

/**
 * Flat, form-friendly representation of a single FHIR `Dosage` element.
 * A medication with more than one step is a taper/titration; each step maps to
 * a `Dosage` element with a `sequence`.
 */
export interface DosageStep {
  /** Dose amount (or the low bound of a range). */
  doseValue: string;
  /** Optional high bound — when present the dose is emitted as a `doseRange`. */
  doseValueHigh: string;
  doseUnit: string;
  /** One of FREQUENCY_PRESETS values (incl. 'custom' / 'one_time_only') or ''. */
  frequencyPreset: string;
  frequencyTimes: string;
  frequencyEvery: string;
  /** Optional upper bound of an interval range ("every 4 to 6 hours"). */
  frequencyEveryMax: string;
  frequencyUnit: string;
  /** Total administrations over the course ("3 doses total") -> repeat.count. */
  frequencyCount: string;
  /** PRN. Additive to the schedule, not a schedule of its own. */
  asNeeded: boolean;
  /** EventTiming codes (MORN | AFT | EVE | HS | AC | PC). */
  timing: string[];
  /** Course-length value, mapped to timing.repeat.boundsDuration. */
  durationValue: string;
  durationUnit: string;
  /** Optional ceiling ("max 8 tablets per day") -> maxDosePerPeriod (Ratio). */
  maxDoseValue: string;
  maxDoseUnit: string;
  /** Denominator count, e.g. "2" in "max 8 tablets per 2 days". Defaults to 1. */
  maxDosePeriodValue: string;
  maxDosePeriodUnit: string;
  /**
   * Administration rate, e.g. an infusion at "100 mL per 1 hour". A rate with a
   * period maps to `rateRatio`, one without to `rateQuantity`.
   */
  rateValue: string;
  rateUnit: string;
  ratePerValue: string;
  ratePerUnit: string;
  /** Free-text dosage instruction ("As directed") -> Dosage.text. */
  asDirected: string;
}

export const emptyDosageStep = (): DosageStep => ({
  doseValue: '',
  doseValueHigh: '',
  doseUnit: '',
  frequencyPreset: '',
  frequencyTimes: '',
  frequencyEvery: '',
  frequencyEveryMax: '',
  frequencyUnit: '',
  frequencyCount: '',
  asNeeded: false,
  timing: [],
  durationValue: '',
  durationUnit: '',
  maxDoseValue: '',
  maxDoseUnit: '',
  maxDosePeriodValue: '',
  maxDosePeriodUnit: '',
  rateValue: '',
  rateUnit: '',
  ratePerValue: '',
  ratePerUnit: '',
  asDirected: '',
});

const DURATION_UNIT_LABELS: Record<string, string> = {
  h: 'hour',
  d: 'day',
  wk: 'week',
  mo: 'month',
  a: 'year',
};

/** True when a step carries any meaningful clinical content. */
export function stepHasContent(step: DosageStep): boolean {
  return Boolean(
    step.doseValue ||
    step.doseValueHigh ||
    step.frequencyPreset ||
    step.asNeeded ||
    step.timing.length > 0 ||
    step.durationValue ||
    step.maxDoseValue ||
    step.frequencyCount ||
    step.rateValue ||
    step.asDirected
  );
}

/** A usable ratio denominator: anything non-positive falls back to one. */
function positiveOr1(value: number | undefined): number {
  return value !== undefined && value > 0 ? value : 1;
}

/** Narrows away both null and undefined; the schema uses both. */
function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function toNumber(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = parseFloat(value);
  // Rejects Infinity too: '1e999' parsed to Infinity, serialised as null, and
  // stripNulls then left a Quantity with a unit and no value.
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Maps a stored unit onto the DOSE_UNITS value. The migration emits label casing
 * ("mEq" where the table keys "meq"), which missed the lookup and so lost the
 * stored UCUM system/code on save and left the dropdown unselected. An unknown
 * unit is returned unchanged rather than blanked.
 */
export function canonicalDoseUnit(raw: string | null | undefined): string {
  if (!raw) return '';
  const lower = raw.trim().toLowerCase();
  const match = DOSE_UNITS.find(
    (u) => u.value.toLowerCase() === lower || u.label.toLowerCase() === lower
  );
  return match?.value ?? raw;
}

function buildQuantity(value: number, unit: string) {
  const def = DOSE_UNITS.find((u) => u.value === unit);
  // Only assert UCUM for a unit that is actually in the table. Defaulting to
  // true fabricated a system/code for anything else, so a legacy 'tab' or a
  // label-cased 'mEq' claimed to be a UCUM code it isn't.
  const isUcum = def?.ucum === true;
  return {
    value,
    unit,
    ...(isUcum && { system: UCUM_SYSTEM, code: def.ucumCode }),
  };
}

function buildRepeat(step: DosageStep): RepeatInput | undefined {
  const repeat: RepeatInput = {};
  const preset = FREQUENCY_PRESETS.find(
    (p) => p.value === step.frequencyPreset
  );

  if (step.frequencyPreset === 'custom') {
    const times = toNumber(step.frequencyTimes);
    if (times !== undefined) repeat.frequency = times;
    const period = toNumber(step.frequencyEvery);
    if (period !== undefined) repeat.period = period;
    // tim-6: periodMax without period is invalid, and writing one produced an
    // element the preview then had to describe as nothing.
    const periodMax = toNumber(step.frequencyEveryMax);
    if (periodMax !== undefined && period !== undefined) {
      repeat.periodMax = periodMax;
    }
    if (step.frequencyUnit) repeat.periodUnit = step.frequencyUnit;
  } else if (preset?.count) {
    // "One time only" is a total administration count, not a rate.
    repeat.count = preset.count;
  } else if (preset && preset.frequency !== null) {
    repeat.frequency = preset.frequency;
    repeat.period = preset.period;
    repeat.periodUnit = preset.periodUnit;
  }

  // A total administration count is independent of the rate: "3 doses total"
  // can accompany a daily schedule. An explicit value wins over a preset's.
  const count = toNumber(step.frequencyCount);
  if (count !== undefined) repeat.count = count;

  if (step.timing.length > 0) repeat.when = step.timing;

  if (step.durationValue && step.durationUnit) {
    const value = toNumber(step.durationValue);
    if (value !== undefined) {
      repeat.boundsDuration = {
        value,
        unit: step.durationUnit,
        system: UCUM_SYSTEM,
        code: step.durationUnit,
      };
    }
  }

  return Object.keys(repeat).length ? repeat : undefined;
}

/** "max 8 tablets per 2 days" -> Ratio of 8 tablet / 2 day. */
function buildMaxDosePerPeriod(step: DosageStep): RatioInput | undefined {
  const value = toNumber(step.maxDoseValue);
  if (value === undefined || !step.maxDoseUnit || !step.maxDosePeriodUnit) {
    return undefined;
  }
  return {
    numerator: buildQuantity(value, step.maxDoseUnit),
    denominator: {
      // An absent or nonsensical period count means "per one of these", the
      // common case. A zero would make the ratio a division by zero.
      value: positiveOr1(toNumber(step.maxDosePeriodValue)),
      unit: step.maxDosePeriodUnit,
      system: UCUM_SYSTEM,
      code: step.maxDosePeriodUnit,
    },
  };
}

/** "100 mL per 1 hour" -> rateRatio; "50 mL/h" with no period -> rateQuantity. */
function buildRate(step: DosageStep): DoseAndRateInput | undefined {
  const value = toNumber(step.rateValue);
  if (value === undefined || !step.rateUnit) return undefined;
  if (step.ratePerUnit) {
    return {
      rateRatio: {
        numerator: buildQuantity(value, step.rateUnit),
        denominator: buildQuantity(
          positiveOr1(toNumber(step.ratePerValue)),
          step.ratePerUnit
        ),
      },
    };
  }
  return { rateQuantity: buildQuantity(value, step.rateUnit) };
}

function periodNoun(
  unit: string | null | undefined,
  count: number | null | undefined = 1
): string {
  if (!unit) return '';
  const noun = PERIOD_UNIT_NOUNS[unit] ?? unit;
  return count === 1 ? noun : `${noun}s`;
}

/**
 * Human-readable frequency phrase from a FHIR `timing.repeat`.
 *
 * Reads the resource rather than the form step so it cannot drift from what a
 * save actually writes.
 */
export function describeRepeat(
  repeat: RepeatInput | Repeat | null | undefined,
  asNeeded = false
): string {
  if (!repeat) return asNeeded ? 'as needed' : '';
  const { frequency, frequencyMax, period, periodMax, periodUnit, count } =
    repeat;
  const suffix = asNeeded ? ' as needed' : '';

  // A total count with no rate: "one time only" at 1, otherwise a dose total.
  if (isPresent(count) && !frequency && !frequencyMax && !isPresent(period)) {
    return count === 1 ? `one time only${suffix}` : `${count} doses${suffix}`;
  }

  if (isPresent(period) && isPresent(periodMax) && periodUnit) {
    return `every ${period} to ${periodMax} ${periodNoun(periodUnit, periodMax)}${suffix}`;
  }

  // A stored count ceiling or range. The form no longer writes `frequencyMax`,
  // but 8 records in the migration data carry one and it has to read correctly.
  if (isPresent(frequencyMax) && periodUnit) {
    const per =
      period === 1
        ? `a ${periodNoun(periodUnit)}`
        : `every ${period} ${periodNoun(periodUnit, period)}`;
    if (isPresent(frequency)) {
      return `${frequency} to ${frequencyMax} times ${per}${suffix}`;
    }
    return frequencyMax === 1
      ? `up to once ${per}${suffix}`
      : `up to ${frequencyMax} times ${per}${suffix}`;
  }

  // A plain preset reads best as its own label ("every other day" beats
  // "every 2 days"). PRN appends to it rather than changing the encoding.
  if (periodUnit) {
    const preset = FREQUENCY_PRESETS.find(
      (p) =>
        p.frequency === frequency &&
        p.period === period &&
        p.periodUnit === periodUnit
    );
    if (preset) return `${preset.label.toLowerCase()}${suffix}`;
  }

  if (period === 1 && isPresent(frequency) && periodUnit) {
    const noun = periodNoun(periodUnit);
    const base =
      frequency === 1 ? `once a ${noun}` : `${frequency} times a ${noun}`;
    return `${base}${suffix}`;
  }
  if (isPresent(period) && period > 1 && periodUnit) {
    const every = `every ${period} ${periodNoun(periodUnit, period)}`;
    if (isPresent(frequency) && frequency > 1) {
      return `${frequency} times ${every}${suffix}`;
    }
    return `${every}${suffix}`;
  }
  if (isPresent(frequency)) {
    return `${frequency} times${suffix}`;
  }
  return asNeeded ? 'as needed' : '';
}

/** Frequency phrase for a form step, via the repeat the step would write. */
export function describeFrequency(step: DosageStep): string {
  return describeRepeat(buildRepeat(step), step.asNeeded);
}

function describeQuantity(
  value: number | null | undefined,
  unit: string | null | undefined
): string {
  if (!isPresent(value)) return '';
  return `${value} ${formatUnitLabel(unit ?? '', value)}`.trim();
}

/**
 * One-line description of a FHIR `Dosage`, used for the save preview and the
 * read view.
 *
 * Deliberately renders the resource rather than the form step, since anything
 * derived from a parallel reading of the step can and did disagree with what
 * gets written: the Timing control was invisible to it, and it reported
 * "Nothing yet" for a step that wrote an element.
 *
 * The edit form's preview passes `freeText: false` so it matches the list card,
 * which means it shows less than the payload — `Dosage.text` and
 * `patientInstruction` are still written. That is why the preview is labelled
 * "Shows in the list as" rather than "Will be saved as".
 */
export interface DescribeDosageOptions {
  /**
   * Whether to append the prose clauses — `patientInstruction` and
   * `Dosage.text`. On by default, since the preview and the read view are
   * showing the whole record.
   *
   * The medications list turns it off: a card is a line to scan, and a
   * sentence of dosing instructions on the end of one crowds out the dose it
   * was there to show. Callers that turn it off need a fallback for a record
   * whose only dose is its free text.
   */
  freeText?: boolean;
  /**
   * Whether to append the route. On by default. The PDF export turns it off:
   * its table prints the route in a column of its own, so leaving it on would
   * end every Dosage cell with the word already sitting beside it.
   */
  route?: boolean;
}

export function describeDosage(
  d: DosageInput | Dosage | undefined,
  { freeText = true, route = true }: DescribeDosageOptions = {}
): string {
  if (!d) return '';
  const parts: string[] = [];

  const dar = d.doseAndRate?.[0];
  const range = dar?.doseRange;
  if (range) {
    const low = range.low?.value;
    const high = range.high?.value;
    const unit = formatUnitLabel(
      range.high?.unit ?? range.low?.unit ?? '',
      high
    );
    if (isPresent(low) && isPresent(high)) {
      parts.push(`${low} to ${high} ${unit}`.trim());
    } else if (isPresent(high)) {
      parts.push(`up to ${high} ${unit}`.trim());
    } else if (isPresent(low)) {
      parts.push(describeQuantity(low, range.low?.unit));
    }
  } else if (dar?.doseQuantity) {
    const dose = describeQuantity(
      dar.doseQuantity.value,
      dar.doseQuantity.unit
    );
    if (dose) parts.push(dose);
  }

  const repeat = d.timing?.repeat;
  const asNeeded = Boolean(d.asNeededBoolean || d.asNeededCodeableConcept);
  const frequency = describeRepeat(repeat, asNeeded);
  if (frequency) parts.push(frequency);

  const when = repeat?.when?.filter((code): code is string => Boolean(code));
  if (when?.length) {
    parts.push(
      when
        .map(
          (code) =>
            TIMING_OPTIONS.find((o) => o.value === code)?.label.toLowerCase() ??
            code
        )
        .join(' and ')
    );
  }

  const bounds = repeat?.boundsDuration;
  if (isPresent(bounds?.value) && bounds.unit) {
    const base = DURATION_UNIT_LABELS[bounds.unit] ?? bounds.unit;
    const label = base && bounds.value !== 1 ? `${base}s` : base;
    parts.push(`for ${bounds.value} ${label}`.trim());
  }

  const rateRatio = dar?.rateRatio;
  const rateQuantity = dar?.rateQuantity;
  if (isPresent(rateRatio?.numerator?.value)) {
    const per = rateRatio.denominator;
    const perValue = per?.value ?? 1;
    const perLabel =
      perValue === 1
        ? periodNoun(per?.unit)
        : `${perValue} ${periodNoun(per?.unit, perValue)}`;
    const amount = describeQuantity(
      rateRatio.numerator.value,
      rateRatio.numerator.unit
    );
    parts.push(`at ${amount}${perLabel ? ` per ${perLabel}` : ''}`);
  } else if (isPresent(rateQuantity?.value)) {
    parts.push(`at ${describeQuantity(rateQuantity.value, rateQuantity.unit)}`);
  }

  const max = d.maxDosePerPeriod;
  if (isPresent(max?.numerator?.value) && max.denominator?.unit) {
    const per = max.denominator.value ?? 1;
    const period =
      per === 1
        ? periodNoun(max.denominator.unit)
        : `${per} ${periodNoun(max.denominator.unit, per)}`;
    const amount = describeQuantity(max.numerator.value, max.numerator.unit);
    parts.push(`max ${amount} per ${period}`);
  }

  if (route && d.route?.text) parts.push(d.route.text);
  if (freeText && d.patientInstruction) parts.push(d.patientInstruction);

  if (!freeText || !d.text) return parts.join(', ');

  // `Dosage.text` is free text that often restates the structured fields — the
  // VEG-535 migration writes a whole generated summary there, and brand-combo
  // records write "1 tablet (500-125mg)" over a 1-tablet dose. Drop any clause
  // the text already states rather than printing it twice. Nothing is hidden:
  // whatever is dropped is visible in the text itself.
  const text = d.text;
  const lower = text.toLowerCase();
  const kept = parts.filter((part) => !lower.includes(part.toLowerCase()));

  return [...kept, text].join(', ');
}

/**
 * The line the medications list prints under a drug name: the product strength,
 * then the structured dosage with the prose left off.
 *
 * Shared with the record model and the edit form's preview so the three can't
 * word the same record differently.
 */
export function summarizeForList(
  dosage: DosageInput | Dosage | undefined,
  strength?: string,
  { route = true }: Pick<DescribeDosageOptions, 'route'> = {}
): string {
  return [strength, describeDosage(dosage, { freeText: false, route })]
    .filter(Boolean)
    .join(', ');
}

/**
 * One-line summary of what a step will be saved as. Renders the `Dosage` the
 * writer produces, so the preview can't disagree with the payload.
 */
export function summarizeStep(
  step: DosageStep,
  route?: string,
  instructions?: string
): string {
  return describeDosage(stepsToDosage([step], { route, instructions })[0]);
}

export interface DosageContext {
  route?: string;
  instructions?: string;
  /**
   * The stored `dosage[]` this save is replacing, matched element by element.
   * A medication update is a full PUT, so anything the form can't express has to
   * be carried across or the server drops it.
   */
  existing?: Dosage[];
}

/**
 * Fields of a `Dosage` the form owns end to end. Each is set from the step or
 * deleted, so clearing a value in the form clears it in the resource. Everything
 * else on the stored element (`site`, `method`, `additionalInstruction`,
 * `maxDosePerAdministration`, `maxDosePerLifetime`, ...) is carried through
 * untouched.
 *
 * Only as far as the query reaches: `DosageFields` doesn't select `Dosage.id`,
 * `extension` or `modifierExtension`, so those are dropped by the full PUT no
 * matter what this merge does.
 *
 * `asNeededCodeableConcept` is here even though no input maps to it: the PRN
 * checkbox owns the whole `asNeeded[x]` choice, so unticking it has to be able to
 * clear a stored reason. `mergeDosage` keeps the reason when PRN stays on.
 */
const FORM_OWNED_DOSAGE_FIELDS = [
  'sequence',
  'doseAndRate',
  'asNeededBoolean',
  'asNeededCodeableConcept',
  'timing',
  'maxDosePerPeriod',
  'route',
  'patientInstruction',
  'text',
] as const;

/**
 * `timing.repeat` fields the form owns; the rest of the stored repeat survives.
 *
 * `frequencyMax` is deliberately absent. The form writes `frequency` for every
 * count and leaves PRN to `asNeededBoolean`, which is how 310 of the 318 PRN
 * records in the migration data are already encoded. The 8 that use
 * `frequencyMax` keep it as passthrough and it still renders in the preview.
 */
const FORM_OWNED_REPEAT_FIELDS = [
  'frequency',
  'period',
  'periodMax',
  'periodUnit',
  'count',
  'countMax',
  'when',
  'boundsDuration',
] as const;

/**
 * FHIR choice groups reachable from a merge. A resource may carry at most one
 * member of each, so writing one has to remove its siblings. Preserving a
 * sibling because "the form doesn't own it" produces an invalid resource.
 *
 * Siblings are only removed when a member is actually written. `boundsPeriod` (a
 * calendar window) and `timeOfDay` (clock times) describe something the form
 * can't express, so an untouched record keeps them.
 */
/** dose[x] and rate[x] within a doseAndRate entry. The form writes both. */
const DOSE_MEMBERS = ['doseQuantity', 'doseRange'] as const;
const RATE_MEMBERS = ['rateRatio', 'rateQuantity', 'rateRange'] as const;

const DOSE_CHOICE_GROUPS: readonly (readonly (keyof DoseAndRateInput)[])[] = [
  DOSE_MEMBERS,
  RATE_MEMBERS,
];

const REPEAT_CHOICE_GROUPS: readonly (readonly (keyof RepeatInput)[])[] = [
  ['boundsDuration', 'boundsPeriod', 'boundsRange'],
  // tim-10: a repeat carries either timeOfDay or when, never both.
  ['when', 'timeOfDay'],
];

function dropChoiceSiblings<T extends object>(
  target: T,
  written: object,
  groups: readonly (readonly (keyof T)[])[]
) {
  for (const group of groups) {
    const set = group.find((field) => field in written);
    if (!set) continue;
    for (const field of group) {
      if (field !== set) delete target[field];
    }
  }
}

/**
 * Merge the form-owned half of a step onto the stored element it replaces.
 * `built` holds only form-owned keys, so a key it omits is one the curator
 * cleared and has to be removed from the result.
 */
function mergeDosage(
  built: DosageInput,
  stored?: Dosage,
  route?: string
): DosageInput {
  const merged = { ...(stored as DosageInput | undefined), ...built };

  for (const field of FORM_OWNED_DOSAGE_FIELDS) {
    if (!(field in built)) delete merged[field];
  }

  // The form owns dose[x] and rate[x] on the first element. `type` and any
  // element past the first belong to whoever wrote them (an infusion order,
  // typically), so the whole array is never replaced.
  //
  // Both halves are form-owned because the form renders both: the rate fields
  // reveal themselves whenever a record carries a rate. So a half the form
  // didn't write is one the curator cleared, and it has to go — leaving a stored
  // rate behind after "Given as an infusion" is unticked saves a rate that is
  // nowhere on screen.
  const clearUnwritten = (
    entry: DoseAndRateInput,
    written: object,
    members: readonly (keyof DoseAndRateInput)[]
  ) => {
    if (members.some((member) => member in written)) return;
    members.forEach((member) => delete entry[member]);
  };

  const storedRate = stored?.doseAndRate;
  if ('doseAndRate' in built) {
    const builtFirst = built.doseAndRate?.[0] ?? {};
    const first = { ...storedRate?.[0], ...builtFirst };
    dropChoiceSiblings(first, builtFirst, DOSE_CHOICE_GROUPS);
    clearUnwritten(first, builtFirst, DOSE_MEMBERS);
    clearUnwritten(first, builtFirst, RATE_MEMBERS);
    const rest = storedRate?.slice(1) ?? [];
    const head = Object.keys(first).length ? [first] : [];
    if (head.length || rest.length) merged.doseAndRate = [...head, ...rest];
    else delete merged.doseAndRate;
  } else if (storedRate?.length) {
    // Both dose and rate cleared in the form: strip both, keep anything else.
    const kept = storedRate
      .map((entry) => {
        const copy = { ...entry } as DoseAndRateInput;
        [...DOSE_MEMBERS, ...RATE_MEMBERS].forEach((member) => {
          delete copy[member];
        });
        return copy;
      })
      .filter((entry) => Object.keys(entry).length > 0);
    if (kept.length) merged.doseAndRate = kept;
    else delete merged.doseAndRate;
  }

  // `route` is form-owned, but its coding isn't expressible in the form and a
  // bare rebuild would drop a stored SNOMED code on every save.
  if (route && stored?.route) {
    merged.route = { ...stored.route, text: route };
  }

  // PRN is still on and the stored record explains why: keep the reason rather
  // than flattening it to a bare boolean, which is the more specific resource
  // and still a single member of asNeeded[x].
  if ('asNeededBoolean' in built && stored?.asNeededCodeableConcept) {
    merged.asNeededCodeableConcept = stored.asNeededCodeableConcept;
    delete merged.asNeededBoolean;
  }

  // `timing` needs the same treatment one level down: the form owns the
  // scheduling fields but not dayOfWeek, offset, or the bounds it can't express.
  const storedRepeat = stored?.timing?.repeat;
  if (storedRepeat) {
    const builtRepeat = built.timing?.repeat;
    if (builtRepeat) {
      const repeat: RepeatInput = { ...storedRepeat, ...builtRepeat };
      for (const field of FORM_OWNED_REPEAT_FIELDS) {
        if (!(field in builtRepeat)) delete repeat[field];
      }
      dropChoiceSiblings(repeat, builtRepeat, REPEAT_CHOICE_GROUPS);
      merged.timing = { ...stored.timing, repeat };
    } else {
      // The form produced no repeat. That happens both when the curator cleared
      // the schedule and when the stored repeat holds only things the form can't
      // author (a bare `frequencyMax`, `dayOfWeek`, `timeOfDay`). Strip the
      // form-owned fields and keep whatever is left, or the stored schedule is
      // deleted outright.
      const kept: RepeatInput = { ...storedRepeat };
      for (const field of FORM_OWNED_REPEAT_FIELDS) delete kept[field];
      // A surviving frequencyMax is meaningless without the period it counts
      // against, so that pair travels with it even though the form owns it.
      if (isPresent(storedRepeat.frequencyMax)) {
        kept.frequencyMax = storedRepeat.frequencyMax;
        if (isPresent(storedRepeat.period)) kept.period = storedRepeat.period;
        if (storedRepeat.periodUnit) kept.periodUnit = storedRepeat.periodUnit;
      }
      const rest: TimingInput = { ...stored.timing };
      delete rest.repeat;
      if (Object.keys(kept).length) rest.repeat = kept;
      if (Object.keys(rest).length) merged.timing = rest;
      else delete merged.timing;
    }
  }

  return merged;
}

/**
 * Build a FHIR `dosage[]` from form steps. Multiple content-bearing steps are
 * emitted with `sequence`. `route` is applied to every element; `instructions`
 * to the first element only (matching how the form surfaces them).
 *
 * Merges onto `context.existing` so a save preserves the parts of a stored
 * `Dosage` the form cannot express. Without that, a full PUT built from form
 * values alone would silently drop `site`, `method`, `additionalInstruction`,
 * `dayOfWeek`, `timeOfDay` and the rest on every edit.
 *
 * `Dosage.text` carries what the curator typed into "As directed", and on the
 * auto-curation path what `medicationOcrAssembler` prefills there for values the
 * form has no field for (an infusion rate, a dose total above one). It is never
 * overwritten with a generated summary of the other fields, which would destroy
 * curator-authored text on every save.
 */
export function stepsToDosage(
  steps: DosageStep[] | undefined,
  { route, instructions, existing }: DosageContext = {}
): DosageInput[] {
  const real = (steps ?? []).filter(stepHasContent);
  const useSequence = real.length > 1;

  const buildOne = (step: DosageStep | undefined, idx: number): DosageInput => {
    const d: DosageInput = {};

    if (step) {
      if (useSequence) d.sequence = idx + 1;

      // Range.low is optional, so "up to 2 tablets" is a valid high-only range.
      const low = toNumber(step.doseValue);
      const high = toNumber(step.doseValueHigh);
      const dose =
        low === undefined && high === undefined
          ? undefined
          : high !== undefined
            ? {
                doseRange: {
                  ...(low !== undefined && {
                    low: buildQuantity(low, step.doseUnit),
                  }),
                  high: buildQuantity(high, step.doseUnit),
                },
              }
            : { doseQuantity: buildQuantity(low as number, step.doseUnit) };
      const rate = buildRate(step);
      // Dose and rate share one doseAndRate entry: "100 mL at 50 mL/h".
      if (dose || rate) d.doseAndRate = [{ ...dose, ...rate }];

      if (step.asNeeded) d.asNeededBoolean = true;

      const repeat = buildRepeat(step);
      if (repeat) d.timing = { repeat };

      const maxDosePerPeriod = buildMaxDosePerPeriod(step);
      if (maxDosePerPeriod) d.maxDosePerPeriod = maxDosePerPeriod;

      if (step.asDirected) d.text = step.asDirected;
    }

    // Route applies to every element and instructions to the first, whether or
    // not that element still has a step behind it.
    if (route) d.route = { text: route };
    if (idx === 0 && instructions) d.patientInstruction = instructions;

    return d;
  };

  // Cover every stored element, not just the steps that still have content: an
  // element carrying only fields the form can't express (site, method,
  // maxDosePerAdministration) would otherwise be deleted wholesale when the
  // curator edits something else. Merged elements that end up empty are dropped.
  const length = Math.max(real.length, existing?.length ?? 0);
  const dosage: DosageInput[] = Array.from({ length }, (_unused, idx) =>
    mergeDosage(buildOne(real[idx], idx), existing?.[idx], route)
  ).filter((d) => Object.keys(d).length > 0);

  // Route and instructions are worth an element of their own even with no dose or
  // frequency. Only reachable when there is no stored element to merge onto,
  // since the loop above already covers those.
  if (!dosage.length && (route || instructions)) {
    const d: DosageInput = {};
    if (route) d.route = { text: route };
    if (instructions) d.patientInstruction = instructions;
    return [d];
  }

  return dosage;
}

/** Inverse of stepsToDosage: hydrate form steps from a FHIR `dosage[]`. */
export function dosageToSteps(
  dosage?: ReadonlyArray<Dosage | null | undefined> | null
): DosageStep[] {
  if (!dosage || !dosage.length) return [emptyDosageStep()];

  return dosage.map((d) => {
    if (!d) return emptyDosageStep();
    const dar = d.doseAndRate?.[0];
    const q = dar?.doseQuantity;
    const r = dar?.doseRange;
    const repeat = d.timing?.repeat;
    // A reason is as much a PRN marker as the boolean, and EMR records commonly
    // carry only the reason. Reading just the boolean showed those as not-PRN.
    const asNeeded = Boolean(d.asNeededBoolean || d.asNeededCodeableConcept);
    // `frequency` is the count regardless of PRN. Folding `frequencyMax` in here
    // collapsed a stored range and rewrote 310 migrated records on first save.
    const times = repeat?.frequency;
    const maxDose = d.maxDosePerPeriod;
    const rateRatio = dar?.rateRatio;
    const rateQuantity = dar?.rateQuantity;
    // Typed as unknown so we can also tolerate a legacy scalar `when` value
    // (pre-codegen schemas modelled it as a single code rather than code[]).
    const whenRaw: unknown = repeat?.when;
    const when = Array.isArray(whenRaw)
      ? whenRaw.filter((w): w is string => Boolean(w))
      : typeof whenRaw === 'string' && whenRaw
        ? [whenRaw]
        : [];

    return {
      doseValue: (q?.value ?? r?.low?.value)?.toString() ?? '',
      doseValueHigh: r?.high?.value?.toString() ?? '',
      doseUnit: canonicalDoseUnit(q?.unit ?? r?.high?.unit ?? r?.low?.unit),
      frequencyPreset: getFrequencyPreset({
        frequency: times,
        period: repeat?.period,
        periodMax: repeat?.periodMax,
        periodUnit: repeat?.periodUnit,
        count: repeat?.count,
      }),
      frequencyTimes: times?.toString() ?? '',
      frequencyEvery: repeat?.period?.toString() ?? '',
      frequencyEveryMax: repeat?.periodMax?.toString() ?? '',
      frequencyUnit: repeat?.periodUnit ?? '',
      frequencyCount: repeat?.count?.toString() ?? '',
      asNeeded,
      timing: when,
      durationValue: repeat?.boundsDuration?.value?.toString() ?? '',
      durationUnit: repeat?.boundsDuration?.unit ?? '',
      maxDoseValue: maxDose?.numerator?.value?.toString() ?? '',
      maxDoseUnit: canonicalDoseUnit(maxDose?.numerator?.unit),
      maxDosePeriodValue: maxDose?.denominator?.value?.toString() ?? '',
      maxDosePeriodUnit: maxDose?.denominator?.unit ?? '',
      rateValue:
        (rateRatio?.numerator?.value ?? rateQuantity?.value)?.toString() ?? '',
      rateUnit: canonicalDoseUnit(
        rateRatio?.numerator?.unit ?? rateQuantity?.unit
      ),
      ratePerValue: rateRatio?.denominator?.value?.toString() ?? '',
      ratePerUnit: rateRatio?.denominator?.unit ?? '',
      asDirected: d.text ?? '',
    };
  });
}

/** Duration unit options for the form (reuses the frequency period units). */
export const DURATION_UNITS = FREQUENCY_PERIOD_UNITS;
